import React from 'react';
import styled from 'styled-components';
import { UserCog } from 'lucide-react';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;
  padding: 0 0 80px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 28px;
  padding: 36px 28px 28px;
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 -4px 40px rgba(0,0,0,0.12);
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const IconCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(45,188,175,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  color: #2DBCAF;
  svg {
    width: 38px;
    height: 38px;
  }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  text-align: center;
  margin: 0;
  line-height: 1.6;
`;

const LoginBtn = styled.button`
  width: 100%;
  height: 52px;
  background: #f1f5f9;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  &:hover { 
    background: #e2e8f0;
  }
`;

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const CancelBtn = styled.button`
  background: none;
  border: none;
  font-size: 15px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  padding: 8px;
  font-family: inherit;
  &:hover { color: #0f172a; }
`;

interface Props {
  onLogin?: () => void;
  onClose?: () => void;
  title?: string;
  subtitle?: string | null;
  description?: string;
  required?: boolean;
}

export default function LoginPromptModal({ onLogin, onClose, title, subtitle, description, required }: Props) {
  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    } else {
      // Pass current path as returnTo so user comes back here after login
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/api/auth/google?returnTo=${returnTo}`;
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const subtitleText = subtitle !== undefined ? subtitle : description;

  return (
    <Overlay onClick={required ? undefined : handleClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <IconCircle>
          <UserCog />
        </IconCircle>
        <Title>{title ?? 'Ups! Kamu Belum Login'}</Title>
        {subtitleText !== null && subtitleText !== '' && (
          <Subtitle>
            {subtitleText ?? <>Silakan login terlebih dahulu untuk<br />menyimpan game favoritmu.</>}
          </Subtitle>
        )}
        <LoginBtn onClick={handleLogin}>
          <GoogleIcon />
          Masuk dengan Google
        </LoginBtn>
        {!required && <CancelBtn onClick={handleClose}>Nanti Saja</CancelBtn>}
      </Modal>
    </Overlay>
  );
}
